import { memo, useEffect, useLayoutEffect, useRef } from 'react';

import type { VirtualizedRow as VirtualizedRowType } from '../model';
import type { ReactElement } from 'react';
import type { DynamicRowHeight, RowComponentProps } from 'react-window';

import styles from './MessagesList.module.css';

type VirtualizedListData = {
  dynamicRowHeight: DynamicRowHeight;
  renderItemComponent: (row: VirtualizedRowType) => ReactElement | null;
  renderMessagesDateSplitter: (row: VirtualizedRowType) => ReactElement | null;
  rows: VirtualizedRowType[];
};

type VirtualizedRowProps = RowComponentProps<VirtualizedListData>;

export const VirtualizedRow = memo(
  ({
    ariaAttributes,
    dynamicRowHeight,
    index,
    renderItemComponent,
    renderMessagesDateSplitter,
    rows,
    style,
  }: VirtualizedRowProps): ReactElement => {
    const row = rows[index];
    const rowRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      const element = rowRef.current;

      if (element !== null && element.scrollHeight !== dynamicRowHeight.getRowHeight(index)) {
        dynamicRowHeight.setRowHeight(index, element.scrollHeight);
      }
    }, [row, index, dynamicRowHeight]);

    useEffect(() => {
      const element = rowRef.current;

      if (element !== null) {
        return dynamicRowHeight.observeRowElements([element]);
      }
    }, [dynamicRowHeight]);

    if (row === undefined) {
      return (
        <div ref={rowRef} className={styles.virtualRow} style={style} {...ariaAttributes}>
          <div className={styles.virtualRowContent} />
        </div>
      );
    }

    return (
      <div ref={rowRef} className={styles.virtualRow} style={style} {...ariaAttributes}>
        <div className={styles.virtualRowContent}>
          {row.type === 'splitter' ? renderMessagesDateSplitter(row) : renderItemComponent(row)}
        </div>
      </div>
    );
  },
);

VirtualizedRow.displayName = 'VirtualizedRow';
